import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { EmploymentSelection } from '../../model/employment-selection.model';
import { environment } from '../../../environments/environment';

/** Shape of an EmploymentSelection as returned by the frontend API (GET). */
interface EmploymentSelectionApiModel {
  employmentSelection_ID: string;
  employmentSelection_Name: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class EmploymentSelectionService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/Employment/EmploymentSelection';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all employment selections for the current context, used to populate
   * the "Persoon selectie" dropdown.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getByContext(): Observable<EmploymentSelection[]> {
    return this.http
      .get<EmploymentSelectionApiModel[] | null>(this.apiUrl + '/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiPayloadToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  private mapApiPayloadToModel(apiModel: EmploymentSelectionApiModel): EmploymentSelection {
    return {
      employmentSelectionId: apiModel.employmentSelection_ID,
      name: apiModel.employmentSelection_Name,
      startDate: apiModel.startDate as EmploymentSelection['startDate'],
      endDate: apiModel.endDate as EmploymentSelection['endDate'],
      memo_Value: apiModel.memo_Value ?? null,
    };
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
